import { useMemo } from "react";
import { motion } from "framer-motion";
import { useAccounting } from "@/contexts/AccountingContext";
import { useNavigate } from "react-router-dom";
import {
  FileText, Receipt, Package, Clock, CheckCircle2, XCircle,
  Plus, IndianRupee, TrendingUp, AlertTriangle, ArrowRight
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const statusConfig: Record<string, { icon: typeof CheckCircle2; className: string }> = {
  Approved: { icon: CheckCircle2, className: "text-success bg-success/10" },
  Pending: { icon: Clock, className: "text-warning bg-warning/10" },
  Rejected: { icon: XCircle, className: "text-destructive bg-destructive/10" },
  Cancelled: { icon: XCircle, className: "text-muted-foreground bg-muted" },
  Draft: { icon: Clock, className: "text-muted-foreground bg-muted" },
};

const AccountantDashboard = () => {
  const { vouchers, stockItems } = useAccounting();
  const navigate = useNavigate();

  const data = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    const todaysEntries = vouchers.filter((v) => v.date === today);
    const pending = vouchers.filter((v) => v.status === "Pending");
    const myRecent = vouchers.slice(0, 8);

    const salesTotal = vouchers.filter((v) => v.type === "Sales" && v.status === "Approved").reduce((s, v) => s + v.totalDebit, 0);
    const purchaseTotal = vouchers.filter((v) => v.type === "Purchase" && v.status === "Approved").reduce((s, v) => s + v.totalDebit, 0);

    const byType = [
      { name: "Sales", value: vouchers.filter((v) => v.type === "Sales").length, color: "hsl(var(--accent))" },
      { name: "Purchase", value: vouchers.filter((v) => v.type === "Purchase").length, color: "hsl(var(--primary))" },
      { name: "Receipt", value: vouchers.filter((v) => v.type === "Receipt").length, color: "hsl(var(--success))" },
      { name: "Payment", value: vouchers.filter((v) => v.type === "Payment").length, color: "hsl(var(--warning))" },
      { name: "Journal", value: vouchers.filter((v) => v.type === "Journal").length, color: "hsl(var(--muted-foreground))" },
      { name: "Contra", value: vouchers.filter((v) => v.type === "Contra").length, color: "hsl(var(--destructive))" },
    ].filter((d) => d.value > 0);

    const lowStock = stockItems.filter((si) => si.reorderLevel && si.currentQty <= si.reorderLevel);

    return { todaysEntries, pending, myRecent, salesTotal, purchaseTotal, byType, lowStock };
  }, [vouchers, stockItems]);

  const weeklyData = useMemo(() => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const result = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split("T")[0];
      const count = vouchers.filter((v) => v.date === dateStr).length;
      result.push({
        day: i === 0 ? "Today" : days[d.getDay()],
        entries: count,
      });
    }
    return result;
  }, [vouchers]);

  const stats = [
    { label: "Entries Today", value: String(data.todaysEntries.length), icon: FileText, color: "text-accent", bg: "bg-accent/10" },
    { label: "Pending Approvals", value: String(data.pending.length), icon: Clock, color: "text-warning", bg: "bg-warning/10" },
    { label: "Sales (Approved)", value: `₹${(data.salesTotal / 100000).toFixed(1)}L`, icon: TrendingUp, color: "text-success", bg: "bg-success/10" },
    { label: "Purchases (Approved)", value: `₹${(data.purchaseTotal / 100000).toFixed(1)}L`, icon: IndianRupee, color: "text-accent", bg: "bg-accent/10" },
  ];

  const quickActions = [
    { label: "Sales Invoice", icon: Receipt, url: "/dashboard/sales", desc: "Create GST invoice" },
    { label: "Purchase Entry", icon: Package, url: "/dashboard/purchase", desc: "Record purchase" },
    { label: "Journal Voucher", icon: FileText, url: "/dashboard/journals", desc: "Journal entry" },
    { label: "View Ledgers", icon: FileText, url: "/dashboard/ledgers", desc: "Browse accounts" },
    { label: "Inventory", icon: Package, url: "/dashboard/inventory", desc: "Manage stock" },
    { label: "GST", icon: Receipt, url: "/dashboard/gst", desc: "Tax compliance" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">My Workspace</h1>
        <p className="text-sm text-muted-foreground">Create entries, manage invoices & track approvals</p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Card>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">{stat.label}</span>
                <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </div>
              <div className="text-2xl font-bold font-display text-foreground">{stat.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickActions.map((action, i) => (
            <button
              key={i}
              onClick={() => navigate(action.url)}
              className="p-4 rounded-lg bg-card border border-border shadow-card hover:border-accent/50 hover:shadow-elevated transition-all text-left group"
            >
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center mb-2 group-hover:bg-accent/20 transition-colors">
                <action.icon className="h-4 w-4 text-accent" />
              </div>
              <span className="text-sm font-medium text-foreground block">{action.label}</span>
              <span className="text-[10px] text-muted-foreground">{action.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Weekly Activity + Voucher Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <h3 className="text-sm font-semibold text-foreground mb-4">Weekly Activity</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="entries" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Voucher Distribution</h3>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={data.byType} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} dataKey="value">
                {data.byType.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-1 mt-2">
            {data.byType.map((d, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                <span className="text-muted-foreground">{d.name}: <strong className="text-foreground">{d.value}</strong></span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Pending + Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Approvals */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-warning" />
              <h3 className="text-sm font-semibold text-foreground">My Pending Entries ({data.pending.length})</h3>
            </div>
          </div>
          <div className="space-y-2">
            {data.pending.slice(0, 5).map((v) => (
              <div key={v.id} className="flex items-center justify-between p-3 rounded-lg bg-warning/5 border border-warning/20">
                <div>
                  <span className="text-sm font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">{v.voucherNumber}</span>
                  <span className="text-xs text-muted-foreground ml-2">{v.type}</span>
                  <p className="text-xs text-muted-foreground mt-0.5">{v.narration.slice(0, 40)}</p>
                </div>
                <div className="text-right">
                  <span className="text-sm font-semibold text-foreground">₹{v.totalDebit.toLocaleString("en-IN")}</span>
                  <div className="text-[10px] text-warning">Awaiting approval</div>
                </div>
              </div>
            ))}
            {data.pending.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">All entries approved ✓</p>}
          </div>
        </Card>

        {/* Low Stock + Stats */}
        <div className="space-y-4">
          {data.lowStock.length > 0 && (
            <Card className="border-warning/30">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-warning" />
                <h3 className="text-sm font-semibold text-foreground">Low Stock Items</h3>
              </div>
              <div className="space-y-2">
                {data.lowStock.map((item) => (
                  <div key={item.id} className="flex items-center justify-between text-xs py-1.5 border-b border-border/30 last:border-0">
                    <span className="text-foreground">{item.name}</span>
                    <span className="text-warning font-medium">{item.currentQty} / {item.reorderLevel} {item.unit}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card>
            <h3 className="text-sm font-semibold text-foreground mb-3">Period Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Total Vouchers</span>
                <span className="font-semibold text-foreground">{vouchers.length}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Approved</span>
                <span className="font-semibold text-success">{vouchers.filter((v) => v.status === "Approved").length}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Rejected</span>
                <span className="font-semibold text-destructive">{vouchers.filter((v) => v.status === "Rejected").length}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Approval Rate</span>
                <span className="font-bold text-foreground">
                  {vouchers.length > 0 ? Math.round((vouchers.filter((v) => v.status === "Approved").length / vouchers.length) * 100) : 0}%
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Recent Entries Table */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-4">Recent Entries</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {["Voucher", "Type", "Party / Narration", "Amount", "Date", "Status"].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.myRecent.map((v) => {
                const sc = statusConfig[v.status] || statusConfig.Draft;
                return (
                  <tr key={v.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 px-3 font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">{v.voucherNumber}</td>
                    <td className="py-2.5 px-3 text-foreground">{v.type}</td>
                    <td className="py-2.5 px-3 text-muted-foreground">{v.partyName || v.narration.slice(0, 35)}</td>
                    <td className="py-2.5 px-3 font-semibold text-foreground">₹{v.totalDebit.toLocaleString("en-IN")}</td>
                    <td className="py-2.5 px-3 text-muted-foreground">{new Date(v.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                    <td className="py-2.5 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${sc.className}`}>
                        <sc.icon className="h-3 w-3" /> {v.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AccountantDashboard;
